// src/pages/Home.jsx
import React, { useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// OPTIMIZATION: Extract components for better organization and reusability
const SearchBar = React.memo(({ searchType, setSearchType, handleSearch, currentUser, isParent, isProvider }) => (
  <div className="home-search-container">
    <form onSubmit={handleSearch} className="home-search-form">
      <select 
        value={searchType} 
        onChange={(e) => setSearchType(e.target.value)}
        className="search-type-select"
        aria-label="Select search type"
      >
        {isParent() || !currentUser ? (
          <>
            <option value="providers">Find Providers</option>
            <option value="jobseekers">Find Caregivers</option>
          </>
        ) : isProvider() ? (
          <>
            <option value="jobseekers">Find Caregivers</option>
          </>
        ) : (
          <>
            <option value="jobs">Find Jobs</option>
          </>
        )}
      </select>
      <button 
        type="submit" 
        className="search-button"
        aria-label="Search"
      >
        Search
      </button>
    </form>
  </div>
));

const HeroCTA = React.memo(({ currentUser, isParent, isProvider, isJobSeeker }) => {
  if (!currentUser) {
    return (
      <div className="hero-cta">
        <Link to="/auth/register" className="cta-button primary">
          Sign Up Now
        </Link>
        <Link to="/auth/login" className="cta-button secondary">
          Log In
        </Link>
      </div>
    );
  }
  
  return (
    <div className="hero-cta">
      {isParent() && (
        <Link to="/parent/search/providers" className="cta-button primary">
          Find Childcare
        </Link>
      )}
      {isProvider() && (
        <Link to="/provider/profile" className="cta-button primary">
          Manage Your Profile
        </Link>
      )}
      {isJobSeeker() && (
        <Link to="/jobseeker/jobs" className="cta-button primary">
          Find Jobs
        </Link>
      )}
    </div>
  );
});

const Home = () => {
  const { currentUser, isProvider, isParent, isJobSeeker } = useAuth();
  const navigate = useNavigate();
  
  // Search state
  const [searchType, setSearchType] = useState('providers');
  
  // OPTIMIZATION: Use useCallback for event handlers
  const handleSearch = useCallback((e) => {
    e.preventDefault();
    
    // Redirect to appropriate search page based on search type
    if (searchType === 'providers') {
      navigate('/parent/search/providers');
    } else if (searchType === 'jobs') {
      navigate('/jobseeker/jobs');
    } else if (searchType === 'jobseekers') {
      navigate('/parent/search/jobseekers');
    }
  }, [searchType, navigate]);
  
  // OPTIMIZATION: Memoize complex sections
  const heroSection = useMemo(() => (
    <section className="hero-section">
      <div className="hero-content">
        <h1>Find the Perfect Childcare Solution</h1>
        <p className="hero-subtitle">
          TotsPal connects parents, childcare providers, and caregivers in one place.
        </p>
        
        <HeroCTA 
          currentUser={currentUser} 
          isParent={isParent} 
          isProvider={isProvider} 
          isJobSeeker={isJobSeeker} 
        />
        
        <SearchBar 
          searchType={searchType}
          setSearchType={setSearchType}
          handleSearch={handleSearch}
          currentUser={currentUser}
          isParent={isParent}
          isProvider={isProvider}
        />
      </div>
      <div className="hero-image">
        <img 
          src="/assets/images/happy_children.jpeg" 
          alt="Happy children and caregivers" 
          loading="eager" 
          width="500" 
          height="300"
        />
      </div>
    </section>
  ), [currentUser, searchType, handleSearch, isParent, isProvider, isJobSeeker]);
  
  const finalCTASection = useMemo(() => (
    <section className="final-cta-section">
      <div className="cta-content">
        <h2>Ready to Find Your Perfect Childcare Match?</h2>
        <p>
          Join thousands of parents, childcare providers, and caregivers on TotsPal.
        </p>
        <Link to="/auth/register" className="cta-button primary">
          Sign Up for Free
        </Link>
      </div>
    </section>
  ), []);
  
  return (
    <div className="home-page no-sidebar">
      {/* Hero Section */}
      {heroSection}
                 
      {/* Final CTA Section */}
      {finalCTASection}
    </div>
  );
};

export default Home;